import React from "react";
import { t } from "utilities/localization-utils";
import DashboardPublicationsTab from "./dashboard-publications-tab";
import DashboardPublicationsTabPanel from "./dashboard-publications-tab-panel";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import TabList from "molecules/tabs/tab-list";
import TabPanels from "molecules/tabs/tab-panels";
import Tabs from "molecules/tabs/tabs";
import usePublicationFavorites from "utilities/hooks/domain/user-publication-favorites/use-publication-favorites";
import PublicationsDisplayList from "molecules/publications-display-list/publications-display-list";
import useOfflinePublications from "utilities/hooks/domain/publications/use-offline-publications";
import useFeaturedBookPublications from "utilities/hooks/domain/publications/use-featured-book-publications";
import { useUserDashboardPreferences } from "utilities/hooks/domain/user-dashboard/use-user-dashboard-preferences";
import { ViewModes } from "models/enumerations/view-modes/view-modes";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint from "utilities/hooks/use-breakpoint";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import { useSortUserPublicationFavorites } from "../../utilities/hooks/domain/user-publication-favorites/use-sort-user-publication-favorites";
import NoResultsBanner from "molecules/banners/no-results-banner";
import { CollectionUtils } from "@rsm-hcd/javascript-core";
import AlertLevels from "constants/alert-levels";
import AlertBanner from "molecules/alerts/alert-banner";
import useServiceWorker, {
    ServiceWorkerStatus,
} from "utilities/contexts/service-worker/use-service-worker";

export const DASHBOARD_PUBS_TABS_BASE_CLASS = "c-dashboard-publications-tabs";
interface DashboardPublicationsTabsProps {
    onOpenPublicationsModal: () => void;
}

const DashboardPublicationsTabs = (props: DashboardPublicationsTabsProps) => {
    const { onOpenPublicationsModal } = props;

    const {
        defaultPublicationsTab: activeTab,
        setDefaultPublicationsTab: onChangeActiveTab,
        defaultPublicationsView: activeView,
        setDefaultPublicationsView: onChangeActiveView,
    } = useUserDashboardPreferences();

    const { isOnline } = useNetworkInformation();

    // Favorites (reusing current hooks for now, may change approach as sorting API work is developed)
    const { favorites: unsortedFavorites, refresh: refreshFavorites } =
        usePublicationFavorites();
    const {
        onFavoritesReordered,
        sortedFavoritePublications,
        sortedFavorites,
    } = useSortUserPublicationFavorites(unsortedFavorites);

    // Offline (reusing current hooks for now, may change approach as sorting API work is developed)
    const { searchResults: offlinePublications } = useOfflinePublications();

    // Featured / New (reusing current hooks for now, may change approach as sorting API work is developed)
    const { publications: featuredPublications } =
        useFeaturedBookPublications();

    const { status: serviceWorkerStatus } = useServiceWorker(); // To be able to display disabled banner

    const featuredPublicationsNotFavorited = featuredPublications.filter(
        (publication) =>
            !sortedFavoritePublications.some(
                (favorited) =>
                    publication.code === favorited.code &&
                    publication.edition === favorited.edition
            )
    );

    // Temporary while we wait for publications modal to be finished
    const handleShowPublicationsModal = () => {
        onOpenPublicationsModal();
    };

    const handleTabClick = async (newVal) => {
        await onChangeActiveTab(newVal);
    };

    const handleViewClick = async (newVal) => {
        onChangeActiveView(newVal);
    };

    const isDesktop = useBreakpoint(Breakpoints.Tablet);

    const view = isDesktop ? activeView : ViewModes.List;
    const showNoOfflinePublicationBanner =
        !isOnline && !CollectionUtils.hasValues(offlinePublications);

    const displayOfflineTab = () => {
        if (serviceWorkerStatus === ServiceWorkerStatus.Disabled) {
            return (
                <AlertBanner alertLevel={AlertLevels.Error}>
                    {t(`serviceWorkerStatus-${serviceWorkerStatus}`)}.
                </AlertBanner>
            );
        } else if (showNoOfflinePublicationBanner) {
            return (
                <NoResultsBanner
                    className={"-secondary"}
                    subtitle={t("offline-dashboard-empty-banner-subtitle")}
                    title={t("offline-books-empty-banner-title")}
                />
            );
        } else {
            return (
                <PublicationsDisplayList
                    favorites={sortedFavorites}
                    noResultsCtaOnClick={handleShowPublicationsModal}
                    noResultsCtaTitle={t("findAPublication")}
                    noResultsSubtitle={t("userDashboard-no-offlines-subtitle")}
                    noResultsTitle={t("userDashboard-no-offlines-title")}
                    onChangeFavorites={refreshFavorites}
                    publications={offlinePublications}
                    view={view}
                />
            );
        }
    };

    return (
        <div className={`${DASHBOARD_PUBS_TABS_BASE_CLASS}`}>
            <Tabs
                defaultIndex={activeTab}
                onChange={(index) => handleTabClick(index)}>
                <TabList>
                    <DashboardPublicationsTab
                        disabled={!isOnline}
                        title={t("favorite_plural")}
                    />
                    <DashboardPublicationsTab title={t("offline")} />
                    <DashboardPublicationsTab
                        disabled={!isOnline}
                        title={t("new")}
                        count={featuredPublicationsNotFavorited.length}
                    />
                </TabList>
                {isDesktop && isOnline && (
                    <div
                        className={`${DASHBOARD_PUBS_TABS_BASE_CLASS}__view-buttons`}>
                        <button
                            type="button"
                            className={`${DASHBOARD_PUBS_TABS_BASE_CLASS}__view-buttons__button${activeView === ViewModes.Grid ? " -active" : ""}`}
                            onClick={() => handleViewClick(ViewModes.Grid)}>
                            <Icon type={Icons.GridView} />
                        </button>
                        <button
                            type="button"
                            className={`${DASHBOARD_PUBS_TABS_BASE_CLASS}__view-buttons__button${activeView === ViewModes.List ? " -active" : ""}`}
                            onClick={() => handleViewClick(ViewModes.List)}>
                            <Icon type={Icons.List} />
                        </button>
                    </div>
                )}
                <TabPanels>
                    <DashboardPublicationsTabPanel>
                        <PublicationsDisplayList
                            allowSorting={true}
                            onFavoritesReordered={onFavoritesReordered}
                            favorites={sortedFavorites}
                            noResultsCtaOnClick={handleShowPublicationsModal}
                            noResultsCtaTitle={t("findAPublication")}
                            noResultsSubtitle={t(
                                "userDashboard-no-favorites-subtitle"
                            )}
                            noResultsTitle={t(
                                "userDashboard-no-favorites-title"
                            )}
                            onChangeFavorites={refreshFavorites}
                            publications={sortedFavoritePublications}
                            view={view}
                        />
                    </DashboardPublicationsTabPanel>
                    <DashboardPublicationsTabPanel>
                        {displayOfflineTab()}
                    </DashboardPublicationsTabPanel>
                    <DashboardPublicationsTabPanel>
                        <PublicationsDisplayList
                            favorites={sortedFavorites}
                            noResultsTitle={t("userDashboard-no-news-title")}
                            onChangeFavorites={refreshFavorites}
                            publications={featuredPublicationsNotFavorited}
                            view={view}
                        />
                    </DashboardPublicationsTabPanel>
                </TabPanels>
            </Tabs>
        </div>
    );
};

export default DashboardPublicationsTabs;
